import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useCommoditiesWithRefresh, useCommoditiesStore } from '@/hooks/useCommodities'
import {
  RefreshCw, TrendingUp, TrendingDown, Gem, ArrowUpRight,
  ArrowDownRight, AlertCircle, Zap, Wheat, BarChart3,
} from 'lucide-react'
import { format } from 'date-fns'
import { safeFormat } from '../lib/format'
import type { CommodityItem, CommodityCategory } from '@/lib/types'

/* ─── Helpers ────────────────────────────────────────────────────────────── */

function fmtPrice(n: number | undefined | null): string {
  if (n == null) return '--'
  if (n >= 1000) return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  if (n >= 10)   return n.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
  return n.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })
}

function PctChip({ v }: { v: number | null | undefined }) {
  if (v == null) return <span className="text-text-muted">--</span>
  const pos = v >= 0
  return (
    <span className={pos ? 'delta-chip-pos' : 'delta-chip-neg'}>
      {pos ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
      {pos ? '+' : ''}{v.toFixed(2)}%
    </span>
  )
}

/* Sparkline (simple SVG) */
function Sparkline({ data, color = '#60a5fa', w = 80, h = 28 }: {
  data: number[]
  color?: string
  w?: number
  h?: number
}) {
  if (!data || data.length < 2) return null
  const min = Math.min(...data)
  const max = Math.max(...data)
  const range = max - min || 1
  const pts = data
    .map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * (h - 2) - 1}`)
    .join(' ')
  return (
    <svg width={w} height={h} className="inline-block">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" />
    </svg>
  )
}

/* 52-week range bar — uses CSS custom property for dynamic fill width */
function RangeBar({ price, low, high }: { price: number; low: number; high: number }) {
  if (!high || !low || high <= low) return null
  const pct = Math.max(0, Math.min(100, ((price - low) / (high - low)) * 100))
  // Dynamic percentage values require a CSS variable; pure Tailwind arbitrary
  // values are static and can't hold runtime data.
  const cssVars = { '--range-pct': `${pct}%`, '--range-dot': `calc(${pct}% - 4px)` } as React.CSSProperties
  return (
    <div className="flex items-center gap-1.5 text-xs text-text-muted">
      <span className="w-10 text-right">{fmtPrice(low)}</span>
      <div className="flex-1 h-1 bg-surface-card rounded-full relative min-w-[48px]" {...{ style: cssVars }}>
        <div className="range-bar-fill absolute top-0 h-1 bg-blue/60 rounded-full [width:var(--range-pct)]" />
        <div className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-blue border border-surface-DEFAULT [left:var(--range-dot)]" />
      </div>
      <span className="w-10">{fmtPrice(high)}</span>
    </div>
  )
}

/* Category icon */
function CategoryIcon({ category }: { category: CommodityCategory }) {
  if (category === 'Metals') return <Gem size={15} className="text-amber-400" aria-hidden />
  if (category === 'Energy') return <Zap size={15} className="text-orange-400" aria-hidden />
  return <Wheat size={15} className="text-lime-400" aria-hidden />
}

/* Commodity card */
function CommodityCard({ item, onClick }: { item: CommodityItem; onClick: () => void }) {
  const pos = item.change_pct >= 0
  const sparkColor = pos ? '#10b981' : '#f43f5e'

  return (
    <div
      className="stat-card card-hover cursor-pointer group"
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-sm font-semibold text-text-primary">{item.name}</p>
          <p className="text-xs text-text-muted">{item.unit} · {item.symbol}</p>
        </div>
        <PctChip v={item.change_pct} />
      </div>

      <div className="flex items-end justify-between">
        <div>
          <p className="text-xl font-bold text-text-primary tabular-nums">
            ${fmtPrice(item.price)}
          </p>
          <p className={`text-xs ${pos ? 'text-emerald-400' : 'text-rose-400'}`}>
            {pos ? '+' : ''}{item.change >= 0 ? '' : ''}{fmtPrice(Math.abs(item.change))} today
          </p>
        </div>
        {item.sparkline.length > 0 && (
          <Sparkline data={item.sparkline} color={sparkColor} w={80} h={32} />
        )}
      </div>

      <div className="mt-2">
        <RangeBar price={item.price} low={item.week_52_low} high={item.week_52_high} />
        <p className="text-xs text-text-muted mt-0.5">52-week range</p>
      </div>
    </div>
  )
}

/* ─── Main Page ──────────────────────────────────────────────────────────── */

export default function CommoditiesPage() {
  const { dashboard, loading, refreshing, error } = useCommoditiesWithRefresh(30_000)
  const { fetch } = useCommoditiesStore()
  const navigate = useNavigate()

  const handleItemClick = (item: CommodityItem) => {
    // Map futures tickers to yfinance chart tickers
    const chartMap: Record<string, string> = {
      'GC=F': 'GC=F', 'SI=F': 'SI=F', 'PL=F': 'PL=F', 'PA=F': 'PA=F', 'HG=F': 'HG=F',
      'CL=F': 'CL=F', 'BZ=F': 'BZ=F', 'NG=F': 'NG=F', 'HO=F': 'HO=F',
      'ZC=F': 'ZC=F', 'ZW=F': 'ZW=F', 'ZS=F': 'ZS=F', 'KC=F': 'KC=F',
      'SB=F': 'SB=F', 'CT=F': 'CT=F',
    }
    const ticker = chartMap[item.symbol] ?? item.symbol
    navigate(`/app/chart?ticker=${encodeURIComponent(ticker)}`)
  }

  const grouped = (dashboard?.grouped ?? {}) as Record<string, CommodityItem[]>
  const topMovers = dashboard?.top_movers ?? []

  const categoryOrder: CommodityCategory[] = ['Metals', 'Energy', 'Agriculture']
  const orderedCategories = categoryOrder.filter(c => grouped[c])

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Gem size={20} className="text-amber-400" aria-hidden />
            Commodities
          </h1>
          <div className="flex items-center gap-2">
            {dashboard && (
              <p className="text-sm text-text-secondary">
                Updated {safeFormat(dashboard.last_updated, 'HH:mm', format)}
              </p>
            )}
            {refreshing && (
              <span className="text-xs text-text-muted flex items-center gap-1">
                <RefreshCw size={11} className="animate-spin" aria-hidden />
                refreshing...
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => fetch(true)}
          className="btn-secondary gap-1.5 text-xs"
          disabled={loading || refreshing}
        >
          <RefreshCw size={13} className={loading || refreshing ? 'animate-spin' : ''} aria-hidden />
          Refresh
        </button>
      </div>

      {error && (
        <div className="card p-4 border border-rose-500/30 bg-rose-500/5 flex items-center gap-2 text-sm">
          <AlertCircle size={15} className="text-rose-400 shrink-0" aria-hidden />
          <span className="text-rose-400">{error}</span>
          {dashboard && <span className="ml-auto text-text-muted text-xs">Showing last known data</span>}
        </div>
      )}

      {/* ── Top Movers ───────────────────────────────────────────────────── */}
      {topMovers.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center gap-2">
            <BarChart3 size={15} className="text-text-muted" aria-hidden />
            <h2 className="panel-title">Top Movers Today</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 p-4 stagger-children">
            {topMovers.map(item => (
              <div
                key={item.symbol}
                className="stat-card card-hover cursor-pointer text-center"
                onClick={() => handleItemClick(item)}
              >
                <p className="text-xs text-text-muted">{item.name}</p>
                <p className="text-sm font-bold text-text-primary mt-1">${fmtPrice(item.price)}</p>
                <PctChip v={item.change_pct} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Categories ───────────────────────────────────────────────────── */}
      {orderedCategories.map(cat => {
        const items = grouped[cat] ?? []
        return (
          <div key={cat} className="card overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center gap-2">
              <CategoryIcon category={cat} />
              <h2 className="panel-title">{cat}</h2>
              <span className="badge-neutral ml-auto">{items.length} contracts</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 p-4 stagger-children">
              {items.map(item => (
                <CommodityCard
                  key={item.symbol}
                  item={item}
                  onClick={() => handleItemClick(item)}
                />
              ))}
            </div>
          </div>
        )
      })}

      {/* ── All Commodities Table ─────────────────────────────────────────── */}
      {dashboard && (
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center gap-2">
            <BarChart3 size={15} className="text-text-muted" aria-hidden />
            <h2 className="panel-title">All Contracts</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="table-pro w-full text-sm">
              <thead>
                <tr>
                  <th>Commodity</th>
                  <th>Category</th>
                  <th className="!text-right">Price</th>
                  <th className="!text-right">Change</th>
                  <th className="!text-right">Change %</th>
                  <th className="!text-right">52w High</th>
                  <th className="!text-right">52w Low</th>
                  <th className="!text-center">Sparkline</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.all.map(item => {
                  const pos = item.change_pct >= 0
                  return (
                    <tr
                      key={item.symbol}
                      className="cursor-pointer"
                      onClick={() => handleItemClick(item)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <CategoryIcon category={item.category} />
                          <div>
                            <p className="font-medium text-text-primary">{item.name}</p>
                            <p className="text-xs text-text-muted">{item.symbol} · per {item.unit}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-text-muted">{item.category}</td>
                      <td className="px-4 py-3 text-right text-text-primary tabular-nums">
                        ${fmtPrice(item.price)}
                      </td>
                      <td className={`px-4 py-3 text-right tabular-nums ${pos ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {pos ? '+' : ''}{fmtPrice(Math.abs(item.change))}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <PctChip v={item.change_pct} />
                      </td>
                      <td className="px-4 py-3 text-right text-text-secondary tabular-nums">
                        ${fmtPrice(item.week_52_high)}
                      </td>
                      <td className="px-4 py-3 text-right text-text-secondary tabular-nums">
                        ${fmtPrice(item.week_52_low)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {item.sparkline.length > 0 && (
                          <Sparkline
                            data={item.sparkline}
                            color={pos ? '#10b981' : '#f43f5e'}
                            w={64}
                            h={24}
                          />
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {!loading && dashboard.all.length === 0 && (
            <p className="p-8 text-center text-text-muted text-sm">No commodities data available.</p>
          )}
        </div>
      )}
    </div>
  )
}
